/**
 * zankap.xyz AI search (Voyage embeddings) with Shopify search fallback.
 *
 * - Header live search: superstore.js asks ZankapSearch.resolveUrl() which
 *   flydown URL to load. If zankap.xyz returns ranked product handles, we load
 *   the search.zankap-header view for those handles; otherwise (error,
 *   timeout, rate limit, no matches) the theme's normal Shopify search URL.
 * - Search results page: page 1 is swapped for zankap.xyz-ranked product
 *   cards (search.zankap-grid view). On any failure the Shopify results stay.
 *
 * After a failure zankap.xyz is skipped for a few minutes so a slow or down
 * backend never adds latency to every keystroke.
 */
(function () {
  'use strict';

  var config = window.ZankapSearchConfig || {};
  if (!config.endpoint) return;

  var TIMEOUT_MS = 1500;
  var DEBOUNCE_MS = 150;
  var BACKOFF_MS = 5 * 60 * 1000;
  var BACKOFF_KEY = 'zankap_search_down_until';
  var HEADER_LIMIT = 5;
  var GRID_LIMIT = 20; // all_products caps at 20 unique handles per request
  var SEPARATOR = '|||';

  // Anonymous per-tab id so the backend can merge keystrokes of one search
  // into a single analytics entry.
  var sessionId = '';
  try {
    sessionId = sessionStorage.getItem('zankap_search_sid') || '';
    if (!sessionId) {
      sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('zankap_search_sid', sessionId);
    }
  } catch (e) {}

  var downUntil = 0;
  try {
    downUntil = Number(sessionStorage.getItem(BACKOFF_KEY)) || 0;
  } catch (e) {}

  function isDown() {
    return Date.now() < downUntil;
  }

  function markDown() {
    downUntil = Date.now() + BACKOFF_MS;
    try {
      sessionStorage.setItem(BACKOFF_KEY, String(downUntil));
    } catch (e) {}
  }

  function searchUrl() {
    return (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search';
  }

  // The theme appends `*` wildcards for Shopify; they mean nothing to Voyage.
  function cleanTerms(terms) {
    return String(terms || '').replace(/\*/g, '').replace(/\s+/g, ' ').trim();
  }

  /**
   * Ranked Shopify handles from zankap.xyz. Resolves to [] on no matches and
   * rejects on any failure (network, timeout, non-200), which triggers backoff.
   */
  function fetchHandles(query, limit, signal, source) {
    var controller = new AbortController();
    var timer = setTimeout(function () { controller.abort(); }, TIMEOUT_MS);
    if (signal) signal.addEventListener('abort', function () { controller.abort(); });

    var url = config.endpoint + '?q=' + encodeURIComponent(query) + '&limit=' + limit +
      '&src=' + (source || 'header');
    if (sessionId) url += '&sid=' + encodeURIComponent(sessionId);
    if (config.customerCode) url += '&code=' + encodeURIComponent(config.customerCode);
    if (config.company) url += '&company=' + encodeURIComponent(config.company);
    return fetch(url, { signal: controller.signal, credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('zankap search ' + res.status);
        return res.json();
      })
      .then(function (data) {
        return (data.results || []).map(function (r) { return r.handle; }).filter(Boolean);
      })
      .catch(function (err) {
        // A newer keystroke cancelling this request is not an outage.
        if (!(signal && signal.aborted)) markDown();
        throw err;
      })
      .finally(function () { clearTimeout(timer); });
  }

  function viewUrl(terms, handles, view) {
    var q = terms + SEPARATOR + handles.join(',');
    return searchUrl() + '?q=' + encodeURIComponent(q) + '&type=product&view=' + view;
  }

  // ── Header live search ──────────────────────────────────────────────────
  var pending = null;

  /**
   * Called by superstore.js Search.execute for the header flydown.
   * Always resolves (never rejects) with the URL the theme should load.
   */
  function resolveUrl(terms, fallbackUrl) {
    if (pending) pending.abort();
    var query = cleanTerms(terms);
    if (!query || isDown()) return Promise.resolve(fallbackUrl);

    var controller = new AbortController();
    pending = controller;

    return new Promise(function (resolve) {
      setTimeout(resolve, DEBOUNCE_MS);
    })
      .then(function () {
        if (controller.signal.aborted) return fallbackUrl;
        return fetchHandles(query, HEADER_LIMIT, controller.signal, 'header').then(function (handles) {
          return handles.length ? viewUrl(terms, handles, 'zankap-header') : fallbackUrl;
        });
      })
      .catch(function () { return fallbackUrl; });
  }

  // ── Modern header suggestions (sections/header-modern.liquid) ─────────────
  /**
   * Products for the header suggest panel, in the same shape as Shopify's
   * /search/suggest.json products. Resolves null whenever the caller should
   * keep Shopify's own suggestions (backend down, timeout, no matches).
   */
  function suggestProducts(terms, limit, signal) {
    var query = cleanTerms(terms);
    if (!query || isDown()) return Promise.resolve(null);
    return fetchHandles(query, limit, signal, 'suggest')
      .then(function (handles) {
        if (!handles.length) return null;
        return Promise.all(handles.map(function (handle) {
          return fetch('/products/' + encodeURIComponent(handle) + '.js', { signal: signal })
            .then(function (res) { return res.ok ? res.json() : null; })
            .catch(function () { return null; });
        }));
      })
      .then(function (products) {
        if (!products) return null;
        var mapped = products.filter(Boolean).map(function (p) {
          return {
            title: p.title,
            url: '/products/' + p.handle,
            vendor: p.vendor,
            price: (p.price / 100).toFixed(2),
            image: p.featured_image
          };
        });
        return mapped.length ? mapped : null;
      })
      .catch(function () { return null; });
  }

  // ── Search results page ─────────────────────────────────────────────────
  function runScripts(container) {
    container.querySelectorAll('script').forEach(function (old) {
      var s = document.createElement('script');
      s.textContent = old.textContent;
      old.parentNode.replaceChild(s, old);
    });
  }

  // Shopify's result count (breadcrumb + tab title) describes its own search,
  // not the AI-ranked products now shown, so rewrite it to match.
  // Rewritten in full (same wording as locales breadcrumbs_count) because
  // Shopify's "No results for …" / "One result for …" variants have no count to patch.
  function updateCounts(count, terms) {
    if (!count) return;
    var crumb = document.querySelector('[data-search-results-count]');
    if (crumb) {
      crumb.textContent = count === 1
        ? 'One result for “' + terms + '”'
        : 'Search results for “' + terms + '” (' + count + ')';
    }
    document.title = document.title.replace(/\d[\d,]* results? found/i, count + (count === 1 ? ' result' : ' results') + ' found');
  }

  function enhanceResultsPage() {
    if (window.location.pathname !== searchUrl()) return;
    var params = new URLSearchParams(window.location.search);
    var terms = params.get('q') || '';
    var query = cleanTerms(terms);
    if (!query || terms.indexOf(SEPARATOR) !== -1 || params.get('view')) return;
    if (params.get('page') && params.get('page') !== '1') return;
    if (isDown()) return;

    var wrapper = document.querySelector('.productgrid--outer .productgrid--wrapper');
    if (!wrapper) return;
    wrapper.classList.add('zankap-search--pending');

    fetchHandles(query, GRID_LIMIT, null, 'page')
      .then(function (handles) {
        if (!handles.length) return;
        return fetch(viewUrl(terms, handles, 'zankap-grid'), { credentials: 'same-origin' })
          .then(function (res) {
            if (!res.ok) throw new Error('zankap grid ' + res.status);
            return res.text();
          })
          .then(function (html) {
            if (!html.trim()) return;
            var grid = wrapper.querySelector('.productgrid--items');
            if (!grid) {
              grid = document.createElement('ul');
              grid.className = 'productgrid--items';
              var noResults = wrapper.querySelector('.productgrid--no-results');
              if (noResults) noResults.parentNode.replaceChild(grid, noResults);
              else wrapper.appendChild(grid);
            }
            grid.innerHTML = html;
            runScripts(grid);
            updateCounts(grid.querySelectorAll('article').length, query);
            // Ranked results are a single page; Shopify's pagination no longer applies.
            wrapper.querySelectorAll('.pagination--container').forEach(function (el) {
              el.style.display = 'none';
            });
          });
      })
      .catch(function (err) {
        console.warn('[zankap-search] using Shopify results:', err && err.message);
      })
      .finally(function () {
        wrapper.classList.remove('zankap-search--pending');
      });
  }

  window.ZankapSearch = { resolveUrl: resolveUrl, suggestProducts: suggestProducts };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceResultsPage);
  } else {
    enhanceResultsPage();
  }
})();
